import { FeatureFlags } from '@issuemanager/adapters/appconfig';
import {
  ActionStatus,
  IssueActionProgressType,
  IssueSeverityTypeWithNull,
  IssueStatusType,
  SLAStatusEnum,
} from '@issuemanager/constants';
import { NumberRangeFilter } from '@issuemanager/database/connection';
import { Issue, UserEntity } from '@issuemanager/models';
import { z } from 'zod';

export const countSchema = z.object({
  count: z.number(),
});

export type SortDirection = 'asc' | 'desc';

export type { NumberRangeFilter };

export type IssueSort = {
  code?: SortDirection;
  issueType?: SortDirection;
  issueSubtype?: SortDirection;
  severity?: SortDirection;
  status?: SortDirection;
  actionProgress?: SortDirection;
  actionStatus?: SortDirection;
  actionCount?: SortDirection;
  relationshipCount?: SortDirection;
  createdAt?: SortDirection;
  updatedAt?: SortDirection;
  dueDate?: SortDirection;
  actualRemediationDate?: SortDirection;
  owner?: SortDirection;
  approver?: SortDirection;
  creator?: SortDirection;
  sourceApplication?: SortDirection;
  sourceEntityName?: SortDirection;
  title?: SortDirection;
  datePublished?: SortDirection;
  resolution?: SortDirection;
  classification?: SortDirection;
  includeInAuditReport?: SortDirection;
  managedByMe?: SortDirection;
};

export type IssueFilter = {
  code?: string | string[];
  title?: string | string[];
  issueType?: string | string[];
  issueSubtype?: string | string[];
  status?: IssueStatusType | IssueStatusType[];
  severity?: IssueSeverityTypeWithNull | IssueSeverityTypeWithNull[];
  actionProgress?: IssueActionProgressType | IssueActionProgressType[];
  actionStatus?: ActionStatus | ActionStatus[];
  actionOwner?: string | string[];
  actionApprover?: string | string[];
  actionCount?: NumberRangeFilter;
  relationshipCount?: number | number[];
  owner?: string | string[];
  approver?: string | string[];
  creator?: string | string[];
  slaDueDays?: NumberRangeFilter;
  slaStatus?: SLAStatusEnum | SLAStatusEnum[];
  createdAtRangeStart?: string;
  createdAtRangeEnd?: string;
  updatedAtRangeStart?: string;
  updatedAtRangeEnd?: string;
  dueDateRangeStart?: string;
  dueDateRangeEnd?: string;
  actualRemediationDateRangeStart?: string;
  actualRemediationDateRangeEnd?: string;
  actionDueDateRangeStart?: string;
  actionDueDateRangeEnd?: string;
  sourceApplication?: string | string[];
  sourceEntityName?: string | string[];
  sourceEntityId?: string | string[];
  sourceEntityType?: string | string[];
  sourceEntityPairs?: Array<{
    sourceEntityId: string;
    sourceEntityType: string;
  }>;
  actionTitle?: string | string[];
  searchTerm?: string;
  resolution?: string | string[];
  classification?: string | string[];
  includeInAuditReport?: boolean | null;
  datePublishedRangeStart?: string;
  datePublishedRangeEnd?: string;
  managedByMe?: boolean;
};

export type SearchIssuesPaginationParams = {
  pageSize?: number;
  pageNumber?: number;
};
export type SearchIssuesQueryParams = {
  sort?: IssueSort;
  filter?: IssueFilter;
  pagination?: SearchIssuesPaginationParams;
  featureFlags?: FeatureFlags | null;
};

export type GetIssuesQueries = {
  searchIssues: (params: SearchIssuesQueryParams) => Promise<{
    results: readonly Issue[];
    count: number;
    page: number;
    pageSize: number;
  }>;
  findUsersByIds: (ids: string[]) => Promise<readonly UserEntity[]>;
};

export type FindIssuesFeature = GetIssuesQueries['searchIssues'];

export type IssuesQueryParams = {
  sort?: IssueSort;
  filter?: IssueFilter;
  pagination?: SearchIssuesPaginationParams;
  featureFlags?: FeatureFlags | null;
};
export type GetIssuesParams = {
  queryParams: IssuesQueryParams;
};

export const getIssues = async (
  params: GetIssuesParams,
  queries: GetIssuesQueries,
): Promise<{
  results: readonly Issue[];
  count: number;
  page: number;
  pageSize: number;
}> => {
  const { queryParams } = params;

  const issues = await queries.searchIssues(queryParams);

  return issues;
};
